import React, { useState, useEffect } from 'react';
import { Wallet, X, CheckCircle, AlertCircle } from 'lucide-react';
import { walletService } from '../../utils/wallet';
import { getUserFriendlyError, logError } from '../../utils/errorHandler';
import logger from '../../utils/logger';

const WalletConnect = ({ onConnect, onDisconnect, isOpen, onClose }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');
  const [walletInfo, setWalletInfo] = useState(null);
  const [holderStatus, setHolderStatus] = useState(null);
  const [availableWallets, setAvailableWallets] = useState([]);

  useEffect(() => {
    const initializeWallets = async () => {
      updateWalletInfo();
      const wallets = await walletService.constructor.getAvailableWallets();
      setAvailableWallets(wallets);

      // Log detected wallets for debugging
      const detectedCount = wallets.filter(w => w.isInstalled && !w.isMobile).length;
      if (detectedCount > 1) {
        logger.log(`🎯 Multiple wallets detected (${detectedCount}):`, wallets.filter(w => w.isInstalled).map(w => w.name).join(', '));
      }

      // Check if we're returning from a wallet redirect
      const walletRedirect = sessionStorage.getItem('wallet_redirect');
      if (walletRedirect) {
        sessionStorage.removeItem('wallet_redirect');
        logger.log('Returning from wallet redirect:', walletRedirect);
      }

      // ❌ DISABLED AUTO-CONNECT - Always show wallet selection menu
      // User explicitly chooses which wallet to connect
      // handleAutoConnect();
    };

    initializeWallets();
  }, []);

  const handleAutoConnect = async () => {
    try {
      // Check for any connected wallet using the wallet service
      const detectedWallets = walletService.constructor.getAllDetectedWallets();
      
      for (const wallet of detectedWallets) {
        const provider = wallet.provider;
        if (provider?.isConnected && provider?.publicKey) {
          logger.log(`Found existing ${wallet.name} connection, reconnecting...`);
          await handleConnect(wallet.id, false);
          break; // Only reconnect to the first connected wallet
        }
      }
    } catch (error) {
      logError('WalletConnect.handleAutoConnect', error);
    }
  };

  const updateWalletInfo = async () => {
    const info = walletService.getWalletInfo();
    setWalletInfo(info);
    
    if (info.isConnected) {
      const status = await walletService.checkTopBlastBalance();
      setHolderStatus(status);
    }
  };

  const handleConnect = async (walletType, isMobile = false) => {
    setIsConnecting(true);
    setError('');
    
    try {
      let result;
      
      // Handle mobile deep link connection
      if (isMobile) {
        result = await walletService.connectMobile(walletType);
        
        if (result.isMobileRedirect) {
          setIsConnecting(false);
          onClose();
          return;
        }
      } else {
        // Use the generic connect method that supports all wallet types
        result = await walletService.connectWallet(walletType);
      }
      
      // Check for TOPBLAST balance with error handling
      let holderCheck = { isHolder: false, balance: 0, formatted: '0' };
      try {
        holderCheck = await walletService.checkTopBlastBalance();
      } catch (balanceError) {
        logError('WalletConnect.handleConnect.balanceCheck', balanceError);
        // Continue with connection even if balance check fails
      }
      
      setWalletInfo(walletService.getWalletInfo());
      setHolderStatus(holderCheck);
      
      if (onConnect) {
        onConnect({
          address: result.address,
          isHolder: holderCheck.isHolder,
          balance: holderCheck.balance,
          formatted: holderCheck.formatted
        });
      }
      
      onClose();
    } catch (err) {
      logError('WalletConnect.handleConnect', err);
      const errorMessage = getUserFriendlyError(err);
      setError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    walletService.disconnect();
    setWalletInfo(null);
    setHolderStatus(null);
    if (onDisconnect) {
      onDisconnect();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Wallet className="w-6 h-6 text-purple-500" />
            <h2 className="text-xl font-bold text-white">
              {walletInfo?.isConnected ? 'Switch Wallet' : 'Connect Wallet'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {!walletInfo?.isConnected ? (
            <>
              {/* Show special message if multiple wallets detected */}
              {availableWallets.filter(w => w.isInstalled && !w.isMobile).length > 1 && (
                <div className="mb-4 p-3 bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-600 rounded-lg">
                  <p className="text-purple-300 text-sm font-bold mb-1">
                    🎉 Multiple wallets detected!
                  </p>
                  <p className="text-purple-200 text-xs">
                    Choose which wallet you'd like to connect below.
                  </p>
                </div>
              )}
              
              <p className="text-gray-400 text-sm mb-4">
                Connect your Solana wallet to verify token holdings and access exclusive TOPBLAST features.
              </p>

              {/* Brave Wallet Info - Only show if NO other wallets detected */}
              {(window.solana?.isBraveWallet || window.braveSolana) && availableWallets.filter(w => w.isInstalled && !w.provider?.isBraveWallet).length === 0 && (
                <div className="mb-4 p-3 bg-orange-900/20 border border-orange-500/50 rounded-lg">
                  <p className="text-orange-300 text-sm font-semibold mb-1">
                    ⚠️ Brave Wallet Not Supported
                  </p>
                  <p className="text-orange-200/80 text-xs">
                    Please install Phantom, Solflare, or Backpack for the best experience.
                  </p>
                </div>
              )}

              {/* Special message for wallet detection */}
              {window.phantom?.solana && !window.phantom.solana.isConnected && (
                <div className="mb-4 p-3 bg-purple-900/20 border border-purple-700 rounded-lg">
                  <p className="text-purple-300 text-sm font-semibold mb-1">🎉 Perfect! You're in Phantom's browser</p>
                  <p className="text-purple-200 text-xs">
                    Now click "Connect Phantom" below to link your wallet to TOPBLAST
                  </p>
                </div>
              )}
              
              {/* Mobile instructions */}
              {walletService.constructor.isMobile() && !window.phantom?.solana && !window.solflare && (
                <div className="mb-4 p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <p className="text-blue-300 text-sm font-semibold mb-1">📱 Mobile Connection:</p>
                  <p className="text-blue-200 text-xs">
                    {walletService.constructor.isIOS() 
                      ? "Tap your preferred wallet above. If you have the app installed, it will open automatically. Otherwise, you'll be redirected to download it."
                      : "After clicking above, you'll be redirected to the wallet's browser. Look for the 'Connect' button to link your wallet."
                    }
                  </p>
                </div>
              )}

              {/* Wallet Connect Buttons */}
              {/* Check for previously connected wallet */}
              {(() => {
                const previouslyConnected = availableWallets.find(w =>
                  w.isInstalled && w.provider?.isConnected && w.provider?.publicKey
                );

                return previouslyConnected && (
                  <div className="mb-4">
                    <p className="text-gray-400 text-xs font-semibold mb-2">PREVIOUSLY CONNECTED</p>
                    <button
                      onClick={() => handleConnect(previouslyConnected.id, false)}
                      disabled={isConnecting}
                      className="w-full py-3 px-6 rounded-lg font-bold transition-all flex items-center justify-between bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed border-2 border-green-400"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{previouslyConnected.icon || '💳'}</span>
                        <span>{previouslyConnected.name}</span>
                      </div>
                      <CheckCircle className="w-5 h-5" />
                    </button>
                    <p className="text-gray-500 text-xs mt-2 text-center">— OR CHOOSE A DIFFERENT WALLET —</p>
                  </div>
                );
              })()}

              <div className="space-y-3">
                {availableWallets.map((wallet) => {
                  const isInstalled = wallet.isInstalled;
                  const isMobileWallet = wallet.isMobile;
                  const isPreviouslyConnected = isInstalled && wallet.provider?.isConnected && wallet.provider?.publicKey;

                  // Skip if this is the previously connected wallet (shown above)
                  if (isPreviouslyConnected) return null;
                  
                  return (
                    <button
                      key={wallet.id}
                      onClick={() => {
                        if (!isInstalled && wallet.downloadUrl) {
                          window.open(wallet.downloadUrl, '_blank');
                        } else {
                          handleConnect(wallet.id, wallet.isMobile);
                        }
                      }}
                      disabled={isConnecting}
                      className={`w-full py-3 px-6 rounded-lg font-bold transition-all flex items-center justify-between ${
                        isInstalled
                          ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white'
                          : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isConnecting ? (
                        <>
                          <svg className="h-6 w-12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 150">
                            <path fill="none" stroke="currentColor" strokeWidth="15" strokeLinecap="round" strokeDasharray="300 385" strokeDashoffset="0" d="M275 75c0 31-27 50-50 50-58 0-92-100-150-100-28 0-50 22-50 50s23 50 50 50c58 0 92-100 150-100 24 0 50 19 50 50Z">
                              <animate attributeName="stroke-dashoffset" calcMode="spline" dur="2" values="685;-685" keySplines="0 0 1 1" repeatCount="indefinite"></animate>
                            </path>
                          </svg>
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center space-x-2">
                            <span className="text-xl">{wallet.icon || '💳'}</span>
                            <span>
                              {!isInstalled && wallet.downloadUrl ? (
                                <>Get {wallet.name}</>
                              ) : isMobileWallet ? (
                                <>Open in {wallet.name} App</>
                              ) : (
                                <>Connect {wallet.name}</>
                              )}
                            </span>
                          </div>
                          {isInstalled && !isMobileWallet && (
                            <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full">
                              Detected
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Backup options for mobile users */}
              <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                <p className="text-gray-300 text-sm font-semibold mb-2">Having trouble connecting?</p>
                <div className="space-y-2">
                  {/* Reconnect button for existing connections */}
                  {availableWallets.some(w => w.isInstalled && !w.isMobile) && (
                    <button
                      onClick={handleAutoConnect}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-3 rounded transition-colors"
                    >
                      🔄 Try Reconnecting
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      navigator.clipboard.writeText(url).then(() => {
                        alert('URL copied! Open Phantom app → Browser → Paste this URL');
                      }).catch(() => {
                        // Fallback for older browsers
                        const dummy = document.createElement('textarea');
                        document.body.appendChild(dummy);
                        dummy.value = url;
                        dummy.select();
                        document.execCommand('copy');
                        document.body.removeChild(dummy);
                        alert('URL copied! Open Phantom app → Browser → Paste this URL');
                      });
                    }}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm py-2 px-3 rounded transition-colors"
                  >
                    📋 Copy URL for Phantom Browser
                  </button>
                  
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>1. Copy this URL</p>
                    <p>2. Open Phantom app → Browser tab</p>
                    <p>3. Paste URL and visit site</p>
                    <p>4. Tap "Connect" to connect your wallet</p>
                    <p className="text-purple-400 mt-2">💡 After connection, you'll be redirected back</p>
                  </div>
                </div>
              </div>


              {error && (
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Connected State */}
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-300 font-semibold">Currently Connected</span>
                </div>
                <p className="text-gray-300 text-sm font-mono">
                  {walletInfo.shortAddress}
                </p>
              </div>

              {/* Holder Status */}
              {holderStatus && (
                <div className={`border rounded-lg p-4 ${
                  holderStatus.isHolder 
                    ? 'bg-purple-900/20 border-purple-700' 
                    : 'bg-gray-800/50 border-gray-700'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">
                      {holderStatus.isHolder ? '💎 TOPBLAST Holder' : '🌱 Future Mover'}
                    </span>
                    {holderStatus.isHolder && (
                      <span className="text-purple-300 text-sm">
                        {holderStatus.formatted} TOPBLAST
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    {holderStatus.isHolder 
                      ? 'Access to exclusive holder features!' 
                      : 'Get some TOPBLAST tokens to unlock holder perks!'
                    }
                  </p>
                </div>
              )}

              {/* Switch Wallet Section */}
              <div className="space-y-3">
                <p className="text-gray-400 text-sm font-semibold">Switch to a different wallet:</p>
                
                {/* Debug: Show all available wallets */}
                {logger.log('Available wallets for switching:', availableWallets)}
                
                {/* Show other available wallets */}
                {availableWallets.filter(w => w.isInstalled && !w.isMobile).map((wallet) => {
                  return (
                    <button
                      key={wallet.id}
                      onClick={async () => {
                        logger.log('🔄 WALLET SWITCH INITIATED to', wallet.name);
                        setIsConnecting(true);
                        setError('');
                        
                        // Store current wallet info in case we need to rollback
                        const previousWalletInfo = walletService.getWalletInfo();
                        const previousHolderStatus = holderStatus;
                        
                        try {
                          
                          // First disconnect current wallet
                          logger.log('1️⃣ Disconnecting current wallet...');
                          walletService.disconnect();
                          
                          // Small delay to ensure clean disconnect
                          await new Promise(resolve => setTimeout(resolve, 100));
                          
                          // Then connect new wallet
                          logger.log('2️⃣ Connecting to', wallet.name, '...');
                          const result = await walletService.connectWallet(wallet.id);
                          logger.log('3️⃣ Connected! New address:', result.address);
                          
                          // Check balance
                          let holderCheck = { isHolder: false, balance: 0, formatted: '0' };
                          try {
                            holderCheck = await walletService.checkTopBlastBalance();
                          } catch (balanceError) {
                            logError('WalletConnect.handleConnect.balanceCheck', balanceError);
                          }
                          
                          // Update local state with new wallet
                          setWalletInfo(walletService.getWalletInfo());
                          setHolderStatus(holderCheck);
                          
                          // Notify parent about disconnection and new connection
                          if (onDisconnect) {
                            logger.log('4️⃣ Notifying parent of disconnect...');
                            onDisconnect();
                          }
                          
                          if (onConnect) {
                            logger.log('5️⃣ Calling onConnect with new wallet:', result.address);
                            onConnect({
                              address: result.address,
                              isHolder: holderCheck.isHolder,
                              balance: holderCheck.balance,
                              formatted: holderCheck.formatted
                            });
                          }
                          
                          logger.log('✅ Wallet switch complete!');
                          // Close modal after successful switch
                          setTimeout(() => onClose(), 500);
                        } catch (err) {
                          logError('WalletConnect.switchWallet', err, { targetWallet: wallet.name });
                          setError(getUserFriendlyError(err));
                          
                          // Try to restore previous wallet connection
                          logger.log('🔄 Attempting to restore previous connection...');
                          try {
                            if (previousWalletInfo && previousWalletInfo.address) {
                              // Try to reconnect to any available wallet
                              const detectedWallets = walletService.constructor.getAllDetectedWallets();
                              let reconnected = false;
                              
                              for (const wallet of detectedWallets) {
                                try {
                                  await walletService.connectWallet(wallet.id);
                                  reconnected = true;
                                  break;
                                } catch (e) {
                                  logError('WalletConnect.switchWallet.reconnect', e, { wallet: wallet.name });
                                }
                              }
                              
                              if (reconnected) {
                                setWalletInfo(walletService.getWalletInfo());
                                setHolderStatus(previousHolderStatus);
                                logger.log('✅ Previous wallet restored');
                              } else {
                                // Keep showing previous wallet info to prevent modal reset
                                setWalletInfo(previousWalletInfo);
                                setHolderStatus(previousHolderStatus);
                                logger.log('⚠️ Keeping previous wallet info in UI');
                              }
                            }
                          } catch (restoreError) {
                            logError('WalletConnect.switchWallet.restore', restoreError);
                            // Keep showing previous wallet info to prevent modal reset
                            setWalletInfo(previousWalletInfo);
                            setHolderStatus(previousHolderStatus);
                          }
                        } finally {
                          setIsConnecting(false);
                        }
                      }}
                      disabled={isConnecting}
                      className="w-full py-3 px-6 rounded-lg font-bold transition-all flex items-center justify-between bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/30 hover:to-pink-600/30 border border-purple-600/50 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{wallet.icon || '💳'}</span>
                        <span>Switch to {wallet.name}</span>
                      </div>
                      <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full">
                        Detected
                      </span>
                    </button>
                  );
                })}
                
                {/* If no other wallets detected, show install options */}
                {availableWallets.filter(w => w.isInstalled && !w.isMobile).length <= 1 && (
                  <div className="text-center py-2">
                    <p className="text-gray-500 text-xs mb-2">No other wallets detected</p>
                    <div className="space-y-2">
                      {availableWallets.filter(w => !w.isInstalled && w.downloadUrl).slice(0, 3).map((wallet) => (
                        <button
                          key={wallet.id}
                          onClick={() => window.open(wallet.downloadUrl, '_blank')}
                          className="w-full py-2 px-4 rounded-lg text-sm bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 transition-all"
                        >
                          Install {wallet.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Error display for switch wallet view */}
              {error && (
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-300 text-sm">{error}</p>
                      {error.includes('Phantom extension') && (
                        <p className="text-red-200 text-xs mt-2">
                          💡 Tip: Go to brave://extensions and disable Phantom temporarily, or use Phantom wallet instead.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Disconnect Button */}
              <button
                onClick={handleDisconnect}
                className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium py-2 px-4 rounded-lg transition-all"
              >
                Disconnect Wallet
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-800">
          <p className="text-gray-500 text-xs text-center">
            Your wallet info is only used for token verification. We never store private keys.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WalletConnect;